import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { MapPin, Phone, ChevronDown, Stethoscope } from "lucide-react";

// Indirizzi reali dal sito dentistasostenibile.com — per ogni studio risponde il numero verde
export const clinics = [
  { city: "Paderno Dugnano", prov: "MI", address: "Via Lamarmora 5, 20037 Paderno Dugnano (MI)", phone: "800 628 192", director: "Dr. Massimo Filippo Gattuso — Medico Chirurgo iscr. Albo Milano, oro-maxillo-facciale, docente Università La Sapienza di Roma · Dr. Franco Basciani — Odontoiatra iscr. Albo Milano" },
  { city: "San Donato Milanese", prov: "MI", address: "Via Gorizia, 20097 San Donato Milanese (MI)", phone: "800 628 192", director: "Dr. Rosario Faranda — Odontoiatra" },
  { city: "Arese", prov: "MI", address: "Via Valera 31, 20044 Arese (MI)", phone: "800 628 192", director: "Dir. Scientifico: Dr. Massimo Filippo Gattuso — Medico Chirurgo" },
  { city: "Seveso", prov: "MB", address: "Via San Martino, 20822 Seveso (MB)", phone: "800 628 192", director: "Dr. Emilio Paolo Riccardi — Odontoiatra iscr. Albo dei Medici Odontoiatri di Cremona" },
  { city: "Novara Centro", prov: "NO", address: "Via Solferino, 28100 Novara (NO)", phone: "800 628 192", director: "Dr.ssa Arjola Grjeta — Odontoiatra iscr. Albo Grosseto" },
  { city: "Chivasso", prov: "TO", address: "Via Gerbido, 10034 Chivasso (TO)", phone: "800 628 192", director: "Dir. Chirurgico e Scientifico: Dr. Massimo Filippo Gattuso" },
  { city: "Seriate", prov: "BG", address: "Via Venezian, 24068 Seriate (BG)", phone: "800 628 192", director: "Dr.ssa Rossella Fenili — Odontoiatra iscr. Albo Bergamo" },
  { city: "Palermo — San Lorenzo", prov: "PA", address: "Via Lanza di Scalea, 90146 Palermo (PA)", phone: "800 628 192", director: "Dr. Giuseppe Prestigiacomo — Odontoiatra iscr. Albo Palermo" },
];

const ClinicCard = ({ c, i, open, onToggle }) => (
  <Reveal delay={i * 0.05}>
    <div className="border-b border-[#D8DDD9]" data-testid={`clinic-item-${i}`}>
      <button
        onClick={onToggle}
        className="group w-full flex items-center justify-between py-6 text-left hover:pl-3 transition-[padding] duration-300"
        data-testid={`clinic-toggle-${i}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-5">
          <span className="font-serif-display font-light text-3xl text-[#385244]/25 w-12">
            {String(i + 1).padStart(2, "0")}
          </span>
          <div>
            <p className="font-serif-display text-2xl md:text-3xl text-[#15201A] group-hover:text-[#385244] transition-colors duration-300">
              {c.city}
            </p>
            <p className="text-sm text-[#5C6B61] mt-1">Provincia di {c.prov} · Direttore sanitario indipendente</p>
          </div>
        </div>
        <ChevronDown
          size={24}
          className={`text-[#C98E6E] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
            data-testid={`clinic-details-${i}`}
          >
            <div className="pb-8 pl-0 md:pl-[68px] grid sm:grid-cols-[1fr_1.2fr] gap-6">
              <div className="space-y-4">
                {c.director && (
                  <p className="flex items-start gap-3 text-[#15201A]" data-testid={`clinic-director-${i}`}>
                    <Stethoscope size={20} className="text-[#385244] mt-0.5 flex-shrink-0" />
                    <span className="text-sm leading-relaxed"><strong>Direzione sanitaria:</strong> {c.director}</span>
                  </p>
                )}
                <p className="flex items-start gap-3 text-[#15201A]">
                  <MapPin size={20} className="text-[#385244] mt-0.5 flex-shrink-0" />
                  <span className="text-base leading-relaxed">{c.address}</span>
                </p>
                <a
                  href="tel:800628192"
                  className="flex items-center gap-3 text-[#385244] font-bold hover:text-[#C98E6E] transition-colors duration-300"
                  data-testid={`clinic-phone-${i}`}
                >
                  <Phone size={20} className="flex-shrink-0" />
                  Numero verde {c.phone}
                </a>
                <a
                  href="#contatti"
                  className="inline-flex items-center rounded-full bg-[#385244] hover:bg-[#2B4035] text-white text-sm font-bold px-6 py-3 transition-colors duration-300 active:scale-95"
                  data-testid={`clinic-cta-${i}`}
                >
                  Prenota qui la tua visita
                </a>
              </div>
              <iframe
                title={`Mappa studio ${c.city}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(c.address)}&output=embed`}
                className="w-full h-52 rounded-2xl border border-border"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid={`clinic-map-${i}`}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </Reveal>
);

export const Network = () => {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section id="studi" className="py-24 md:py-32 px-6 md:px-12" data-testid="network-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-14 items-start">
        <div className="lg:sticky lg:top-32">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#7D9285] mb-4">La rete</p>
            <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#15201A]">
              8 studi dentistici <em className="italic text-[#385244]">convenzionati</em> vicino a te: provincia di Milano e di Monza Brianza, Novara, Chivasso (TO), Seriate (Bergamo) e Palermo
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#5C6B61]">
              Ogni studio della rete è una struttura autonoma, con propria partita IVA e un
              direttore sanitario indipendente che risponde in prima persona della qualità delle cure.
              La convenzione garantisce lo stesso protocollo clinico e lo stesso prezzo trasparente ovunque.
              Apri la scheda dello studio più vicino per indirizzo, mappa e telefono.
            </p>
            <img
              src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=70&w=900"
              alt="Studio dentistico moderno con riunito"
              loading="lazy"
              decoding="async"
              className="mt-10 rounded-[40px] rounded-tl-none w-full h-64 object-cover"
            />
          </Reveal>
        </div>
        <div className="flex flex-col">
          {clinics.map((c, i) => (
            <ClinicCard key={c.city} c={c} i={i} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? null : i)} />
          ))}
        </div>
      </div>
    </section>
  );
};
